import { Graphics, Text, Sprite, AnimatedSprite } from '@pixi/react';
import { getZIndex } from '../../../utils/zindex.utils';
import { useEffect, useState, useMemo } from 'react';
import { ITile } from '../../../types/types';
import { computeAllTileIndices } from '../../../utils/tiles-index.utils';
import { useFloorTilesets } from '../../../hooks/useTileSprites';
import {
  tileWidth,
  tileHeight,
  TilesetMapping,
} from '../../../data/tileset.config';
import { useGameStore } from '../../../store/gameSlice';
import { useDevtoolStore } from '../../../store/devtoolSlice';
import Placeholder from '../Placeholder';
import { missingAssetService } from '../../../services/MissingAssetService';

interface EnvironmentProps {
  tiles: ITile[];
  mapWidth: number;
  mapHeight: number;
  tilesetMapping: TilesetMapping;
}

export default ({
  tiles,
  mapWidth,
  mapHeight,
  tilesetMapping,
}: EnvironmentProps) => {
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const showTileIndex = useDevtoolStore((s) => s.showTileIndex);
  const showChessboard = useDevtoolStore((s) => s.showChessboard);
  const showCoordinates = useDevtoolStore((s) => s.showCoordinates);
  const {
    isLoaded,
    getTexture,
    getAnimFrames,
    getAnimSpeed,
    getLayers,
    getArtConfig,
  } = useFloorTilesets(tilesetMapping);
  const [tilesIndex, setTilesIndex] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    setTilesIndex(computeAllTileIndices(tiles, mapWidth, mapHeight));
  }, [tiles, mapWidth, mapHeight]);

  const renderTiles = useMemo(() => {
    if (tilesIndex.size === 0) {
      return null;
    }

    return tiles.map((tile) => {
      const key = `${tile.x},${tile.y}`;
      const autoTileIndex = tilesIndex.get(key) ?? 1;
      // Anchor at the tile's bottom-center (sprite anchor defaults to [0.5, 1])
      // so art can overflow upward without shifting the feet. For anchor=[0.5,1]
      // and art=1×1 this y is mathematically equivalent to a tile-centered render.
      const tileBottomCenterX = tile.x * tileWidth * zoomFactor;
      const tileBottomCenterY =
        (-tile.y * tileHeight + tileHeight / 2) * zoomFactor;

      const elements = [];

      if (isLoaded) {
        const layers = getLayers(tile.type);

        for (let li = 0; li < layers.length; li++) {
          // Base layers render as full tile (pattern 47 = all neighbors).
          // Only the top layer uses the actual autotile bitmask.
          const patternId = li < layers.length - 1 ? 47 : autoTileIndex;
          const layerName = layers[li];
          const animSpeed = getAnimSpeed(layerName);
          const frames =
            animSpeed > 0 ? getAnimFrames(patternId, layerName) : null;
          const art = getArtConfig(layerName);
          const w = art.width * tileWidth * zoomFactor;
          const h = art.height * tileHeight * zoomFactor;

          if (frames && frames.length > 1) {
            elements.push(
              <AnimatedSprite
                key={`env-${tile.x}-${tile.y}-L${li}`}
                textures={frames}
                isPlaying={true}
                animationSpeed={animSpeed}
                loop={true}
                initialFrame={0}
                anchor={art.anchor}
                x={tileBottomCenterX}
                y={tileBottomCenterY}
                width={w}
                height={h}
                zIndex={getZIndex({
                  type: 'environment',
                  posY: tile.y,
                  envType: tile.type,
                })}
              />,
            );
          } else {
            const texture = getTexture(patternId, layerName);
            if (texture) {
              elements.push(
                <Sprite
                  key={`env-${tile.x}-${tile.y}-L${li}`}
                  texture={texture}
                  anchor={art.anchor}
                  x={tileBottomCenterX}
                  y={tileBottomCenterY}
                  width={w}
                  height={h}
                  zIndex={getZIndex({
                    type: 'environment',
                    posY: tile.y,
                    envType: tile.type,
                  })}
                />,
              );
            } else {
              missingAssetService.report(layerName, 'tile');
              elements.push(
                <Placeholder
                  key={`env-${tile.x}-${tile.y}-L${li}`}
                  x={tileBottomCenterX}
                  y={tileBottomCenterY}
                  width={w}
                  height={h}
                  anchor={art.anchor as [number, number]}
                  zIndex={getZIndex({
                    type: 'environment',
                    posY: tile.y,
                    envType: tile.type,
                  })}
                />,
              );
            }
          }
        }
      }

      if (showTileIndex) {
        elements.push(
          <Text
            key={`debug-${tile.x}-${tile.y}`}
            text={autoTileIndex.toString()}
            anchor={0.5}
            x={tile.x * tileWidth * zoomFactor}
            y={-tile.y * tileHeight * zoomFactor}
            zIndex={getZIndex({ type: 'debug' })}
            style={
              {
                fontSize: 8 * zoomFactor,
                fill: 0x000000,
                fontWeight: 'bold',
              } as any
            }
          />,
        );
      }

      return elements;
    });
  }, [
    tilesIndex,
    tiles,
    zoomFactor,
    isLoaded,
    showTileIndex,
    getLayers,
    getAnimSpeed,
    getAnimFrames,
    getArtConfig,
    getTexture,
  ]);

  const renderChessboard = useMemo(() => {
    if (!showChessboard && !showCoordinates) {
      return null;
    }

    const hw = (tileWidth * zoomFactor) / 2;
    const hh = (tileHeight * zoomFactor) / 2;

    const result = [];

    if (showChessboard) {
      result.push(
        <Graphics
          key="grid-lines"
          zIndex={getZIndex({ type: 'debug' }) - 1}
          draw={(g: any) => {
            g.clear();
            g.lineStyle(1, 0xffffff, 0.5);
            for (let x = 0; x < mapWidth; x++) {
              for (let y = 0; y < mapHeight; y++) {
                g.drawRect(
                  x * tileWidth * zoomFactor - hw,
                  -y * tileHeight * zoomFactor - hh,
                  tileWidth * zoomFactor,
                  tileHeight * zoomFactor,
                );
              }
            }
          }}
        />,
      );
    }

    if (showCoordinates) {
      for (let x = 0; x < mapWidth; x++) {
        for (let y = 0; y < mapHeight; y++) {
          result.push(
            <Text
              key={`pos-${x}-${y}`}
              text={`${x},${y}`}
              anchor={[1, 0]}
              x={x * tileWidth * zoomFactor + hw - 2}
              y={-y * tileHeight * zoomFactor - hh + 2}
              zIndex={getZIndex({ type: 'debug' })}
              style={
                {
                  fontSize: 4 * zoomFactor,
                  fill: 0x000000,
                } as any
              }
            />,
          );
        }
      }
    }

    return result;
  }, [showChessboard, showCoordinates, mapWidth, mapHeight, zoomFactor]);

  // Flat output (no wrapping Container) so that each tile sprite is a direct
  // child of the parent's sortableChildren container — this lets a tile at
  // map y=N depth-sort against entities/items at the same y.
  return (
    <>
      {renderTiles}
      {renderChessboard}
    </>
  );
};
